import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Radio-card utility (TDU) selector for the Texas landing page (Design Specification Section 7.1/7.2).
 * Deliberately does not show customer counts, region size, or phone numbers from /api/tdus (Design
 * Specification F5: "Never render" utility customer counts on this page — Utilities.jsx is the one place
 * that detail is in scope). "Plans available" / "Coming soon" is derived from whether the utility has at
 * least one verified plan, passed in by the caller; a coming-soon utility stays selectable (AC-TX acceptance:
 * a zone with no verified plans still leads to the plans page, which shows its own coming-soon state).
 */
export default function UtilitySelector({ value, onChange, availability = {} }) {
  const [tdus, setTdus] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | error

  useEffect(() => {
    let cancelled = false;
    api
      .get("/tdus", { params: { state: "TX" } })
      .then((r) => { if (!cancelled) { setTdus(r.data || []); setStatus("ok"); } })
      .catch(() => { if (!cancelled) setStatus("error"); });
    return () => { cancelled = true; };
  }, []);

  if (status === "error") {
    return (
      <p role="alert" className="text-sm text-[#B91C1C]" data-testid="utility-selector-error">
        We could not load the list of Texas utility areas. Please try again.
      </p>
    );
  }

  return (
    <fieldset data-testid="utility-selector">
      <legend className="font-semibold text-[#0A2540] mb-3">Choose your utility area.</legend>
      {status === "loading" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-[#EEF2F7] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Utility area">
          {tdus.map((t) => {
            const available = availability[t.id] !== false; // unknown = assume available, never assert "coming soon" without data
            const checked = value === t.id;
            return (
              <label
                key={t.id}
                className={`flex items-start gap-3 rounded-xl border p-4 min-h-[44px] cursor-pointer transition-colors ${
                  checked ? "border-[#15803D] bg-[#F0FDF4]" : "border-[#64748B] bg-white hover:bg-[#F7F9FC]"
                }`}
                data-testid={`utility-option-${t.id}`}
              >
                <input
                  type="radio"
                  name="utility-area"
                  value={t.id}
                  checked={checked}
                  onChange={() => onChange(t.id)}
                  className="mt-1 w-5 h-5 shrink-0"
                  data-testid={`utility-radio-${t.id}`}
                />
                <span>
                  <span className="block font-semibold text-[#0A2540]">{t.name}</span>
                  <span className={`block text-xs mt-0.5 font-medium ${available ? "text-[#166534]" : "text-[#92400E]"}`}>
                    {available ? "Plans available" : "Coming soon"}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}
